/**
 * TLS-mode client for the setup wizard + cluster settings.
 *
 * `POST /api/system/tls` accepts a discriminated body (the "new TLS model"):
 *
 *   { mode: "cloudflare", cfMode: "flexible", originLock? }
 *   { mode: "cloudflare", cfMode: "full", fullCertSource: "self-signed",
 *       originLock?, mtls?: { enabled, zoneCaPem? } }
 *   { mode: "cloudflare", cfMode: "full", fullCertSource: "origin-ca",
 *       originCaToken, originLock?, mtls?: { enabled, zoneCaPem? } }
 *   { mode: "byo", cert, key }
 *   { mode: "acme", email? }
 *
 * The response carries an `operationId` for the flows that kick a background
 * issuance Operation the UI streams: `acme` (Let's Encrypt warm-up) and
 * `cloudflare` + `full` + `origin-ca` (Cloudflare Origin CA issuance).
 *
 * We hit the endpoint with a raw `fetch` (not the Eden treaty client) so the
 * frontend is decoupled from the exact compile-time shape of the route while
 * the backend lands the new model in parallel. The server is the source of
 * truth for validation; this wrapper just normalizes the response envelope
 * and surfaces a friendly error message.
 */

export type TlsMode = "cloudflare" | "byo" | "acme";
export type CfMode = "flexible" | "full";
export type CfFullCertSource = "self-signed" | "origin-ca";

/** mTLS / Authenticated Origin Pulls — Cloudflare + Full only. */
export interface TlsMtls {
	enabled: boolean;
	/** Optional zone-level origin-pull CA PEM. Omitted = bundled CF global CA. */
	zoneCaPem?: string;
}

export type TlsRequest =
	| { mode: "cloudflare"; cfMode: "flexible"; originLock?: boolean }
	| {
			mode: "cloudflare";
			cfMode: "full";
			fullCertSource: "self-signed";
			originLock?: boolean;
			mtls?: TlsMtls;
	  }
	| {
			mode: "cloudflare";
			cfMode: "full";
			fullCertSource: "origin-ca";
			originCaToken: string;
			originLock?: boolean;
			mtls?: TlsMtls;
	  }
	| { mode: "byo"; cert: string; key: string }
	| { mode: "acme"; email?: string };

export interface TlsResponse {
	success: boolean;
	tlsMode: string;
	cfMode?: CfMode;
	cfFullCertSource?: CfFullCertSource;
	cfOriginLock?: boolean;
	originMtlsEnabled?: boolean;
	/**
	 * Present for the flows that kick a background issuance Operation:
	 * `acme` and `cloudflare` + `full` + `origin-ca`.
	 */
	operationId?: string;
}

export async function postTls(body: TlsRequest): Promise<TlsResponse> {
	const res = await fetch("/api/system/tls", {
		method: "POST",
		credentials: "include",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(body),
	});

	let json: unknown = null;
	try {
		json = await res.json();
	} catch {
		// fall through — handled below
	}

	if (!res.ok || !isOkEnvelope(json)) {
		throw new Error(extractError(json, res.status));
	}
	return json as TlsResponse;
}

/**
 * Current TLS configuration, read from `GET /api/system/setup-status`. Raw
 * fetch (not Eden) so the settings/wizard UI can read the new discriminated
 * fields before the backend's typed serializer catches up.
 */
export interface TlsConfig {
	tlsMode: TlsMode | string;
	cfMode: CfMode;
	cfFullCertSource: CfFullCertSource;
	cfOriginLock: boolean;
	originMtlsEnabled: boolean;
}

export async function getTlsConfig(): Promise<TlsConfig | null> {
	const res = await fetch("/api/system/setup-status", {
		credentials: "include",
	});
	let json: unknown = null;
	try {
		json = await res.json();
	} catch {
		return null;
	}
	if (!res.ok || !isOkEnvelope(json)) return null;
	const v = json as Record<string, unknown>;
	return {
		tlsMode: typeof v.tlsMode === "string" ? v.tlsMode : "cloudflare",
		cfMode: v.cfMode === "full" ? "full" : "flexible",
		cfFullCertSource:
			v.cfFullCertSource === "origin-ca" ? "origin-ca" : "self-signed",
		// Origin lock defaults on; only an explicit `false` disables it.
		cfOriginLock: v.cfOriginLock !== false,
		originMtlsEnabled: v.originMtlsEnabled === true,
	};
}

/**
 * The active origin certificate, from `GET /api/system/tls/certificate`.
 * Returns `null` on 404 (no origin cert — e.g. cloudflare-flexible). PEM is
 * the certificate only; the private key is never exposed.
 */
export interface TlsCertificate {
	pem: string;
	fingerprint: string;
	notAfter: string;
	source: string;
}

export async function getTlsCertificate(): Promise<TlsCertificate | null> {
	const res = await fetch("/api/system/tls/certificate", {
		credentials: "include",
	});
	if (res.status === 404) return null;
	let json: unknown = null;
	try {
		json = await res.json();
	} catch {
		throw new Error(`HTTP ${res.status}`);
	}
	if (!res.ok || !isOkEnvelope(json)) {
		throw new Error(extractError(json, res.status));
	}
	const v = json as Record<string, unknown>;
	return {
		pem: typeof v.pem === "string" ? v.pem : "",
		fingerprint: typeof v.fingerprint === "string" ? v.fingerprint : "",
		notAfter: typeof v.notAfter === "string" ? v.notAfter : "",
		source: typeof v.source === "string" ? v.source : "",
	};
}

function isOkEnvelope(value: unknown): boolean {
	return (
		!!value &&
		typeof value === "object" &&
		(value as { success?: unknown }).success === true
	);
}

function extractError(value: unknown, status: number): string {
	if (value && typeof value === "object") {
		const v = value as { error?: { message?: string }; message?: string };
		if (v.error?.message) return v.error.message;
		if (v.message) return v.message;
	}
	return `HTTP ${status}`;
}

// ---------------------------------------------------------------------------
// Issuance timelines (acme + Cloudflare Origin CA) — shared scaffold.
// ---------------------------------------------------------------------------

export interface IssuanceStageDef {
	key: string;
	label: string;
	/** Substrings matched (case-insensitive) against a backend step key/label. */
	match: string[];
}

export interface IssuanceErrorInfo {
	title: string;
	hint: string;
}

/**
 * Copy + classifier bundle that parametrizes the shared `IssuanceProgress`
 * timeline for a given issuance flow.
 */
export interface IssuanceCopy {
	/** Card heading, e.g. "Issuing certificate". */
	heading: string;
	/** Canonical stage scaffold shown before the backend emits real steps. */
	stages: IssuanceStageDef[];
	successTitle: string;
	successBody: string;
	retryLabel: string;
	classify: (text: string, hostname: string | null) => IssuanceErrorInfo;
}

/** The four canonical ACME warm-up stages shown in the timeline. */
export const ACME_STAGES: IssuanceStageDef[] = [
	{ key: "dns", label: "Checking DNS", match: ["dns", "resolve", "record"] },
	{
		key: "request",
		label: "Requesting certificate",
		match: ["request", "issue", "order", "acme", "challenge", "cert"],
	},
	{
		key: "install",
		label: "Installing",
		match: ["install", "reload", "load", "apply"],
	},
	{ key: "live", label: "Live", match: ["live", "verify", "serve", "ready"] },
];

/** Canonical Cloudflare Origin CA issuance stages. */
export const ORIGIN_CA_STAGES: IssuanceStageDef[] = [
	{
		key: "token",
		label: "Validating token",
		match: ["token", "auth", "verify"],
	},
	{
		key: "issue",
		label: "Requesting Origin CA certificate",
		match: ["request", "issue", "order", "origin", "cert", "api"],
	},
	{
		key: "install",
		label: "Installing",
		match: ["install", "reload", "load", "apply", "write"],
	},
	{ key: "live", label: "Live", match: ["live", "serve", "ready"] },
];

/**
 * Classify a failed ACME issuance into an actionable, inline message. Scans
 * the failed step's error plus the operation log for well-known signatures.
 */
export function classifyAcmeError(
	text: string,
	hostname: string | null,
): IssuanceErrorInfo {
	const t = text.toLowerCase();
	const host = hostname ?? "this domain";

	if (
		/nxdomain|does not resolve|no a record|doesn't resolve|not resolve|resolution failed|no such host|dns/.test(
			t,
		)
	) {
		return {
			title: `DNS for ${host} doesn't point to this host`,
			hint: "Add an A record for the domain pointing at this server's public IP, wait for it to propagate, then retry.",
		};
	}
	if (
		/port\s*80|:80\b|http-01|\.well-known|acme-challenge|connection refused|timed out|timeout|unreachable|econnrefused/.test(
			t,
		)
	) {
		return {
			title: "Port 80 / 443 looks unreachable",
			hint: "Let's Encrypt must reach this host on ports 80 and 443. Open them in your firewall / security group and retry.",
		};
	}
	if (/rate ?limit|too many certificates|429|toomanyrequests/.test(t)) {
		return {
			title: "Let's Encrypt rate limit hit",
			hint: "Too many certificate requests for this domain recently. Wait before retrying (up to ~1 hour), or use a different hostname.",
		};
	}
	return {
		title: "Certificate issuance failed",
		hint: "Check the DNS A record and that ports 80/443 are open, then retry. See the details above for the underlying error.",
	};
}

/**
 * Classify a failed Cloudflare Origin CA issuance. The common failure is a
 * bad / insufficiently-scoped API token; network + API errors follow.
 */
export function classifyOriginCaError(
	text: string,
	_hostname: string | null,
): IssuanceErrorInfo {
	const t = text.toLowerCase();

	if (
		/401|403|unauthor|forbidden|invalid token|bad token|authentication|permission|scope|9109|1000/.test(
			t,
		)
	) {
		return {
			title: "Cloudflare rejected the API token",
			hint: "The token must have the Zone → SSL and Certificates → Edit permission. Create a fresh token in the Cloudflare dashboard and paste it again.",
		};
	}
	if (
		/timed out|timeout|unreachable|connection refused|econnrefused|dns|network/.test(
			t,
		)
	) {
		return {
			title: "Couldn't reach the Cloudflare API",
			hint: "This host needs outbound HTTPS to api.cloudflare.com. Check egress/firewall rules and retry.",
		};
	}
	if (/rate ?limit|429|too many/.test(t)) {
		return {
			title: "Cloudflare rate limit hit",
			hint: "Too many Origin CA requests recently. Wait a bit before retrying.",
		};
	}
	return {
		title: "Origin CA issuance failed",
		hint: "Verify the API token has SSL & Certificates edit access and that this host can reach the Cloudflare API, then retry. See the details above.",
	};
}

export const ACME_COPY: IssuanceCopy = {
	heading: "Issuing certificate",
	stages: ACME_STAGES,
	successTitle: "Certificate is live",
	successBody:
		"A Let's Encrypt certificate was issued and installed. HTTPS is now serving on this host.",
	retryLabel: "Retry issuance",
	classify: classifyAcmeError,
};

export const ORIGIN_CA_COPY: IssuanceCopy = {
	heading: "Issuing Origin CA certificate",
	stages: ORIGIN_CA_STAGES,
	successTitle: "Origin certificate is live",
	successBody:
		"A 15-year Cloudflare Origin CA certificate was issued and installed. Set your Cloudflare SSL mode to Full (strict) to require it.",
	retryLabel: "Retry issuance",
	classify: classifyOriginCaError,
};
